import { useRef, useState } from 'react';
import { Upload, Loader2, CheckCircle, Video, Send, MessageCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import type { StaffVideoTask } from '@/services/data/staffVideoContent';
import { uploadMonteurVideo, transitionToEditingReview } from '@/services/data/monteurActions';
import { toast } from 'sonner';
import { StaffChatThread } from './StaffChatThread';


interface MonteurDetailDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly task: StaffVideoTask;
  readonly onDone?: () => void;
}

export function MonteurDetailDialog({ open, onOpenChange, task, onDone }: Readonly<MonteurDetailDialogProps>) {
  const slotInputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null]);
  const [slotUploading, setSlotUploading] = useState<Record<string, number | null>>({});
  const [slotDone, setSlotDone] = useState<Record<string, boolean>>(() => ({
    video_1: !!task.video_1_url,
    video_2: !!task.video_2_url,
    video_3: !!task.video_3_url,
  }));
  const [isSending, setIsSending] = useState(false);

  const clientName = [task.client_first_name, task.client_last_name].filter(Boolean).join(' ') || 'Client';
  const slots = task.is_multihook ? [0, 1, 2] : [0];

  const handleSlotUpload = async (slotIndex: number, file: File) => {
    if (!task.shooting_id) return;
    const slot = `video_${slotIndex + 1}` as 'video_1' | 'video_2' | 'video_3';
    setSlotUploading(prev => ({ ...prev, [slot]: 0 }));

    const result = await uploadMonteurVideo(
      task.shooting_id,
      task.id,
      file,
      slot,
      (progress) => setSlotUploading(prev => ({ ...prev, [slot]: progress }))
    );

    if (result.success) {
      setSlotUploading(prev => ({ ...prev, [slot]: null }));
      setSlotDone(prev => ({ ...prev, [slot]: true }));
      toast.success(`Vidéo ${slotIndex + 1} uploadée`);
    } else {
      setSlotUploading(prev => ({ ...prev, [slot]: null }));
      toast.error(result.error || 'Erreur upload');
    }
  };

  const allRequiredSlots = task.is_multihook ? ['video_1', 'video_2', 'video_3'] : ['video_1'];
  const allSlotsDone = allRequiredSlots.every(s => slotDone[s]);
  const isAnyUploading = Object.values(slotUploading).some(v => v !== null && v !== undefined);

  const handleSendToReview = async () => {
    setIsSending(true);
    const result = await transitionToEditingReview(task.id);
    if (result.success) {
      toast.success('Envoyé en validation');
      onDone?.();
    } else {
      toast.error(result.error || 'Erreur');
    }
    setIsSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !isAnyUploading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-3xl bg-[#1a1a1a] border-white/10 p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="p-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-white leading-tight">
                {task.title || 'Vidéo sans titre'}
              </h2>
              <p className="text-sm text-white/50 mt-1">{clientName}</p>
            </div>
            {task.is_multihook && (
              <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 text-purple-400 text-xs font-medium">
                <Video className="h-3 w-3" />
                Multihook
              </span>
            )}
          </div>
          {task.earliest_publication_date && (
            <p className="text-xs text-white/40 mt-2">
              📅 Publication prévue le {new Date(task.earliest_publication_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })}
            </p>
          )}
        </div>

        <Separator className="bg-white/10" />

        {/* Two-column layout */}
        <div className="flex min-h-[340px]">
          {/* Left: Discussion */}
          <div className="flex-1 p-5 flex flex-col border-r border-white/10">
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-3 flex items-center gap-1.5">
              <MessageCircle className="h-3 w-3" />
              Discussion
            </p>
            <div className="flex-1 min-h-0">
              <StaffChatThread videoContentId={task.id} />
            </div>
          </div>

          {/* Right: Upload + Validation */}
          <div className="w-[280px] flex-shrink-0 p-5 flex flex-col">
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-3">
              Upload vidéo{task.is_multihook ? 's montées' : ' montée'}
            </p>

            <div className="space-y-2.5 flex-1">
              {slots.map((slotIndex) => {
                const slot = `video_${slotIndex + 1}`;
                const progress = slotUploading[slot];
                const done = slotDone[slot];
                const uploading = progress !== null && progress !== undefined;
                const label = task.is_multihook ? `Variation ${slotIndex + 1}` : 'Vidéo finale';

                let existingUrl: string | null = null;
                if (slotIndex === 0) {
                  existingUrl = task.video_1_url;
                } else if (slotIndex === 1) {
                  existingUrl = task.video_2_url;
                } else {
                  existingUrl = task.video_3_url;
                }
                const fileName = existingUrl ? existingUrl.split('/').pop() : null;

                let buttonClassName: string;
                if (done) {
                  buttonClassName = 'bg-emerald-500/10 border border-emerald-500/25 text-emerald-400';
                } else if (uploading) {
                  buttonClassName = 'bg-white/5 border border-white/10 text-white/70';
                } else {
                  buttonClassName = 'bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:border-white/20 cursor-pointer';
                }

                return (
                  <div key={slot}>
                    <input
                      ref={el => { slotInputRefs.current[slotIndex] = el; }}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleSlotUpload(slotIndex, file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      onClick={() => slotInputRefs.current[slotIndex]?.click()}
                      disabled={uploading || done}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all disabled:cursor-default ${buttonClassName}`}
                    >
                      <div className="flex-shrink-0">
                        {uploading && <Loader2 className="h-4 w-4 animate-spin text-warning" />}
                        {!uploading && done && <CheckCircle className="h-4 w-4 text-emerald-400" />}
                        {!uploading && !done && <Upload className="h-4 w-4 text-warning" />}
                      </div>
                      <div className="flex-1 text-left text-xs">
                        <span>{label}</span>
                        {uploading && (
                          <div className="mt-1 w-full bg-white/10 rounded-full h-1">
                            <div
                              className="bg-warning h-1 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                      {uploading && (
                        <span className="text-[10px] text-white/50 tabular-nums">{progress}%</span>
                      )}
                    </button>
                    {done && fileName && (
                      <p className="mt-1 px-1 text-[10px] text-white/40 truncate" title={fileName}>
                        {fileName}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {(() => {
              const canSendToReview = allSlotsDone && !['editing_review', 'redelivery_review', 'client_review', 'client_feedback', 'scheduled', 'published'].includes(task.status);

              if (canSendToReview) {
                return (
                  <button
                    onClick={handleSendToReview}
                    disabled={isSending}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm font-semibold hover:bg-emerald-500/25 hover:border-emerald-500/40 transition-all disabled:opacity-50"
                  >
                    {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Envoyer en review
                  </button>
                );
              }

              if (allSlotsDone) {
                return (
                  <div className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400/70 text-sm font-medium">
                    <CheckCircle className="h-4 w-4" />
                    En cours de validation
                  </div>
                );
              }

              return null;
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
